import {
  Notification,
  UsersNotificationType,
} from "@/models/notification/Notifications";
import {
  parseLegislationEntityPath,
  parseWorkspaceCommentEntityPath,
  parseWorkspaceDocumentEntityPath,
  parseWorkspaceEntityPath,
  parseWorkspaceFolderEntityPath,
} from "@/utils/entityPathParser";
import { NavigateFunction } from "react-router-dom";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import { PATH_KEYS } from "./appServices";
import { QueryClient } from "@tanstack/react-query";
import { openAppNavigation } from "@/lib/navigation/appNavigation";
import AppRoutes from "@/constants/routes/AppRoutes";

export const typesThatDoesntNavigate = [
  UsersNotificationType.WorkspaceAccessRevoked,
  UsersNotificationType.DocumentUpdated,
  UsersNotificationType.CommentAdded,
  UsersNotificationType.ConversationStarted,
  UsersNotificationType.MessageReceived,
  UsersNotificationType.ReviewCompleted,
  UsersNotificationType.SystemAlert,
  UsersNotificationType.UserMentioned,
];

export async function navigateToNotificationEntity(
  notification: Notification,
  navigate: NavigateFunction,
  queryClient: QueryClient,
  event?: React.MouseEvent | MouseEvent
) {
  const resetWorkspaceStore = () =>
    useWorkspaceDocumentVersionStore
      .getState()
      .resetWorkspaceDocumentVersionStore();

  switch (notification.type) {
    case "WorkspaceDocumentUploaded":
    case "WorkspaceDocumentsUploaded":
    case "WorkspaceDocumentUpdated": {
      const { workspaceId, documentId } = parseWorkspaceDocumentEntityPath(
        notification.entityPath
      );

      openAppNavigation({
        target: {
          kind: "workspaceDocument",
          workspaceId,
          documentId,
        },
        navigate,
        event,
        onWorkspaceDocumentNavigate: resetWorkspaceStore,
      });
      break;
    }

    case "DocumentLinkAutomaticallyUpdated": {
      const { externalId, datasource } = parseLegislationEntityPath(
        notification.entityPath
      );
      openAppNavigation({
        target: {
          kind: "legislation",
          documentId: externalId,
          source: datasource,
          context: "app",
        },
        navigate,
        event,
      });
      break;
    }

    case "WorkspaceUpdated":
    case "WorkspaceFolderRemoved":
    case "WorkspaceDocumentRemoved":
    case "WorkspaceAccessGranted":
    case "WorkspaceRoleUpdated": {
      const { workspaceId } = parseWorkspaceEntityPath(notification.entityPath);
      openAppNavigation({
        target: { kind: "workspaceFiles", workspaceId },
        navigate,
        event,
      });
      break;
    }

    case "WorkspaceFolderMoved":
    // The API sends "workspace/{id}/folder/" (empty tail) for a move to the root, which
    // parseWorkspaceEntityPath refuses — it is a folder path, so it navigates like one.
    case "WorkspaceFolderMovedToRoot":
    case "WorkspaceDocumentsMoved":
    case "WorkspaceFolderUpdated":
    case "WorkspaceFolderAdded":
    case "WorkspaceLinkAdded":
    case "WorkspaceLinkUpdated":
    case "WorkspaceLinkMoved":
    case "WorkspaceLinkMovedToRoot":
    case "WorkspaceLinkRemoved": {
      const { workspaceId, folderIds } = parseWorkspaceFolderEntityPath(
        notification.entityPath
      );
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.workspace, workspaceId],
      });

      if (folderIds.length === 0) {
        openAppNavigation({
          target: { kind: "workspaceFiles", workspaceId },
          navigate,
          event,
        });
      } else {
        const folderPath = folderIds
          .map((name) => encodeURIComponent(name))
          .join("/");
        openAppNavigation({
          target: {
            kind: "workspaceFilesFolder",
            workspaceId,
            folderPath,
          },
          navigate,
          event,
        });
      }
      break;
    }

    case "WorkspaceCommentUpdated":
    case "WorkspaceCommentRepliedTo":
    case "WorkspaceCommentReacted":
    case "WorkspaceCommentCreated": {
      const { workspaceId, commentId, fileId } =
        parseWorkspaceCommentEntityPath(notification.entityPath);

      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.comment, workspaceId, fileId],
      });

      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.comment, "workspace", workspaceId, "general"],
      });

      if (fileId !== "workspace") {
        openAppNavigation({
          target: {
            kind: "workspaceComments",
            workspaceId,
            documentId: fileId,
            scrollToCommentId: commentId,
          },
          navigate,
          event,
        });
        break;
      }

      openAppNavigation({
        target: { kind: "workspaceComments", workspaceId },
        navigate,
        event,
      });
      break;
    }

    case "LegislationUpdated":
    case "LegislationCreated":
    case "LegislationIsHistoric":
    case "LegislationMetadataUpdated":
    case "LegislationTimelineChanged":
    case "LegislationSubscriptionUpdated": {
      const { externalId, datasource } = parseLegislationEntityPath(
        notification.entityPath
      );
      openAppNavigation({
        target: {
          kind: "legislation",
          documentId: externalId,
          source: datasource,
          context: "app",
        },
        navigate,
        event,
      });
      break;
    }

    case "PiiOverride": {
      // entityPath is the conversation id; jump to the Insights chat overview.
      navigate(AppRoutes.insightsUserOverview(notification.entityPath, "false"));
      break;
    }

    default:
      if (typesThatDoesntNavigate.includes(notification.type)) {
        break;
      }
      break;
  }
}
